/**
 * CategoryHeader Component
 * - Light-grey breadcrumb strip
 * - Purple hero banner with category emoji, big headline + subheadline
 *
 * Banner content per category is sourced from `config/categoryBanner.js`.
 * Falls back to the category name when the slug isn't in the map.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft } from 'lucide-react';
import { Breadcrumbs } from './CategoryFilters';
import { getCategoryBanner } from '../../config/categoryBanner';

const CategoryHeader = ({
  slug,
  pageTitle,
  isCommunity = false,
  getCategoryDisplayName,
  onPostToCommunity
}) => {
  const navigate = useNavigate();
  const banner = getCategoryBanner(slug);

  // Fallback used when slug isn't in the map (e.g. /category/all, search results)
  const display = banner || {
    emoji: '',
    label: pageTitle,
    headline: pageTitle,
    subheadline: '',
  };

  return (
    <>
      {/* Breadcrumb strip - light grey */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <Breadcrumbs slug={slug} getCategoryNameFromSlug={getCategoryDisplayName} />
        </div>
      </div>

      {/* Purple hero banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4">
          {/* Back button (mobile only) */}
          <button
            onClick={() => navigate(-1)}
            className="lg:hidden flex items-center text-blue-100 hover:text-white mb-3"
            data-testid="category-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </button>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="max-w-3xl">
              {/* Small label: emoji + uppercase category name */}
              <div
                className="flex items-center gap-2 text-sm md:text-base font-medium uppercase tracking-wider text-blue-100 mb-2"
                data-testid="category-banner-label"
              >
                {display.emoji && <span className="text-xl md:text-2xl">{display.emoji}</span>}
                <span>{display.label}</span>
              </div>

              {/* Headline */}
              <h1
                className="font-bold leading-tight"
                style={{ fontSize: '2rem' }}
                data-testid="category-page-title"
              >
                {display.headline}
              </h1>

              {/* Subheadline */}
              {display.subheadline && (
                <p
                  className="mt-3 text-base md:text-lg text-blue-50/90 max-w-2xl"
                  data-testid="category-banner-subheadline"
                >
                  {display.subheadline}
                </p>
              )}
            </div>

            {/* Post to Community Button */}
            {isCommunity && (
              <Button
                onClick={onPostToCommunity || (() => navigate(`/community/post?category=${slug}`))}
                className="bg-white text-blue-600 hover:bg-blue-50 self-start md:self-auto"
                data-testid="post-to-community-btn"
              >
                Post to Community
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryHeader;
